import type { CurrentUser } from "../models/current-user";
import type { EntityWithId } from "./shared";

export const RealtimeChangeType = {
	Added: 1,
	Updated: 2,
	Deleted: 3,
	Error: 4,
} as const;

export type RealtimeChangeTypeValue = (typeof RealtimeChangeType)[keyof typeof RealtimeChangeType];

export interface RealtimeChangeMessage<TPayload = unknown> {
	readonly payload: TPayload;
	readonly type: RealtimeChangeTypeValue;
}

/** SignalR hub CRUD: 1 add, 2 update, 3 remove, 4 error. Not protocol Invocation type. */
export const normalizeRealtimeChangeType = (value: unknown): RealtimeChangeTypeValue | null => {
	const numeric = typeof value === "number" ? value : typeof value === "string" ? Number(value) : Number.NaN;
	if (
		numeric === RealtimeChangeType.Added ||
		numeric === RealtimeChangeType.Updated ||
		numeric === RealtimeChangeType.Deleted ||
		numeric === RealtimeChangeType.Error
	) {
		return numeric;
	}
	return null;
};

const asObjectRecord = (value: unknown): Record<string, unknown> | null => {
	if (!value || typeof value !== "object" || Array.isArray(value)) {
		return null;
	}
	return value as Record<string, unknown>;
};

const readChangeType = (record: Record<string, unknown>): unknown => record.type ?? record.Type;

const readChangePayload = (record: Record<string, unknown>): unknown => record.payload ?? record.Payload;

/**
 * Accept hub `{ type, payload }`, `(type, payload)` args, or a raw Invocation frame
 * `{ type: 1, target: "changes", arguments: [{ type, payload }] }`.
 */
export const parseRealtimeChangeMessage = (input: unknown): RealtimeChangeMessage<RealtimeEntityPayload> | null => {
	if (Array.isArray(input)) {
		for (const item of input) {
			const parsed = parseRealtimeChangeMessage(item);
			if (parsed) {
				return parsed;
			}
		}
		return null;
	}
	const record = asObjectRecord(input);
	if (!record) {
		return null;
	}
	if (typeof record.target === "string" && Array.isArray(record.arguments)) {
		return parseRealtimeChangeMessage(record.arguments);
	}
	const nested = asObjectRecord(readChangePayload(record));
	const wrappedType = normalizeRealtimeChangeType(readChangeType(record));
	if (wrappedType && nested) {
		return { type: wrappedType, payload: nested as RealtimeEntityPayload };
	}
	const entityName = record.cosmosEntityName ?? record.CosmosEntityName ?? record.entityName ?? record.EntityName;
	if (entityName === "notifications") {
		return {
			type: record.deleted === true ? RealtimeChangeType.Deleted : RealtimeChangeType.Added,
			payload: record as RealtimeEntityPayload,
		};
	}
	return null;
};

export const parseRealtimeChangeArgs = (
	args: readonly unknown[]
): RealtimeChangeMessage<RealtimeEntityPayload> | null => {
	const fromFirst = parseRealtimeChangeMessage(args[0] ?? null);
	if (fromFirst) {
		return fromFirst;
	}
	if (args.length >= 2) {
		const type = normalizeRealtimeChangeType(args[0]);
		const payload = asObjectRecord(args[1]);
		if (type && payload) {
			return { type, payload: payload as RealtimeEntityPayload };
		}
	}
	return null;
};

export interface RealtimeEntityRef {
	readonly id?: string;
}

export interface RealtimeEntityPayload {
	readonly cosmosEntityName?: string;
	readonly entityName?: string;
	readonly id?: string;
	readonly partitionKey?: unknown;
	readonly user?: RealtimeEntityRef;
	readonly workspace?: RealtimeEntityRef;
	readonly [key: string]: unknown;
}

interface EntityStoreApi {
	readonly useStore: {
		readonly getState: () => {
			readonly upsertOne: (entity: EntityWithId & Record<string, unknown>) => void;
			readonly removeOne: (id: string) => void;
		};
	};
}

interface UserStoreApi {
	readonly useStore: {
		readonly getState: () => {
			readonly value: CurrentUser | null;
			readonly setValue: (value: CurrentUser) => void;
		};
	};
}

/** Minimal stores surface needed for realtime upserts. */
export interface RealtimeStores {
	readonly clients: EntityStoreApi;
	readonly projects: EntityStoreApi;
	readonly tags: EntityStoreApi;
	readonly tasks: EntityStoreApi;
	readonly user: UserStoreApi;
	readonly workspaces: EntityStoreApi;
}

const ENTITY_STORE_KEYS = ["projects", "tasks", "clients", "tags", "workspaces"] as const;

type EntityStoreKey = (typeof ENTITY_STORE_KEYS)[number];

const isEntityStoreKey = (value: string): value is EntityStoreKey =>
	(ENTITY_STORE_KEYS as readonly string[]).includes(value);

const asSettingsRecord = (value: unknown): Record<string, unknown> => asObjectRecord(value) ?? {};

const WORKSPACE_REF_ONLY_KEYS = new Set(["id", "Id", "name", "cosmosEntityName", "entityName", "partitionKey"]);

const isWorkspaceRefOnly = (workspace: Record<string, unknown>): boolean => {
	const keys = Object.keys(workspace);
	if (keys.length === 0) {
		return true;
	}
	return keys.every((key) => WORKSPACE_REF_ONLY_KEYS.has(key));
};

const readEntityId = (value: Record<string, unknown> | null | undefined): string | null => {
	if (!value) {
		return null;
	}
	if (typeof value.id === "string" && value.id.length > 0) {
		return value.id;
	}
	if (typeof value.Id === "string" && value.Id.length > 0) {
		return value.Id;
	}
	return null;
};

const readStringField = (value: Record<string, unknown>, keys: readonly string[]): string | null => {
	for (const key of keys) {
		const field = value[key];
		if (typeof field === "string" && field.length > 0) {
			return field;
		}
	}
	return null;
};

const mergeWorkspaceSnapshot = (
	previous: Record<string, unknown>,
	incoming: Record<string, unknown> | null
): Record<string, unknown> => {
	if (!incoming) {
		return previous;
	}
	if (isWorkspaceRefOnly(incoming)) {
		const id = readEntityId(incoming);
		const name = typeof incoming.name === "string" ? incoming.name : previous.name;
		return {
			...previous,
			...(id ? { id } : {}),
			...(typeof name === "string" ? { name } : {}),
		};
	}
	const id = readEntityId(incoming) ?? readEntityId(previous);
	return {
		...previous,
		...incoming,
		...(id ? { id } : {}),
		settings: {
			...asSettingsRecord(previous.settings),
			...asSettingsRecord(incoming.settings),
		},
	};
};

/** Keep GET /currentuser workspace + settings when a user row only has a thin workspace ref. */
export const mergeCurrentUserSnapshot = (previous: CurrentUser, incoming: Record<string, unknown>): CurrentUser => {
	const previousWorkspace = asObjectRecord(previous.workspace) ?? {};
	return {
		...previous,
		...(incoming as CurrentUser),
		id: previous.id,
		settings: {
			...asSettingsRecord(previous.settings),
			...asSettingsRecord(incoming.settings),
		},
		workspace: mergeWorkspaceSnapshot(previousWorkspace, asObjectRecord(incoming.workspace)),
	};
};

export const resolveRealtimeEntityName = (payload: RealtimeEntityPayload | null | undefined): string | null => {
	if (!payload) {
		return null;
	}
	const record = payload as Record<string, unknown>;
	return readStringField(record, ["cosmosEntityName", "CosmosEntityName", "entityName", "EntityName"]);
};

const payloadOwnerMatchesUser = (payload: RealtimeEntityPayload, userId: string, workspaceId: string): boolean => {
	if (payload.workspace?.id !== workspaceId) {
		return false;
	}
	const ownerId = payload.user?.id;
	if (typeof ownerId === "string" && ownerId.length > 0) {
		return ownerId === userId;
	}
	return true;
};

const isTimesPayloadForUser = (payload: RealtimeEntityPayload, userId: string, workspaceId: string): boolean =>
	resolveRealtimeEntityName(payload) === "times" && payloadOwnerMatchesUser(payload, userId, workspaceId);

const applyUserChange = (stores: RealtimeStores, payload: RealtimeEntityPayload, currentUserId: string): void => {
	const payloadId = readEntityId(payload as Record<string, unknown>);
	if (!payloadId || payloadId !== currentUserId) {
		return;
	}
	const current = stores.user.useStore.getState().value;
	if (!current) {
		return;
	}
	stores.user.useStore.getState().setValue(mergeCurrentUserSnapshot(current, payload as Record<string, unknown>));
};

const applyWorkspaceUserPatch = (
	stores: RealtimeStores,
	payload: RealtimeEntityPayload,
	currentWorkspaceId: string
): void => {
	const payloadId = readEntityId(payload as Record<string, unknown>);
	if (!payloadId || payloadId !== currentWorkspaceId) {
		return;
	}
	const current = stores.user.useStore.getState().value;
	if (!current) {
		return;
	}
	const previousWorkspace = asObjectRecord(current.workspace) ?? {};
	const next: CurrentUser = {
		...current,
		workspace: mergeWorkspaceSnapshot(previousWorkspace, payload as Record<string, unknown>),
	};
	stores.user.useStore.getState().setValue(next);
};

const applyGenericEntityChange = (
	stores: RealtimeStores,
	type: RealtimeChangeTypeValue,
	payload: RealtimeEntityPayload,
	entityName: EntityStoreKey,
	currentWorkspaceId: string
): void => {
	if (entityName !== "workspaces") {
		const payloadWorkspaceId = payload.workspace?.id;
		if (payloadWorkspaceId !== currentWorkspaceId) {
			return;
		}
	}

	const store = stores[entityName].useStore.getState();
	const entityId = readEntityId(payload as Record<string, unknown>);

	if (type === RealtimeChangeType.Deleted) {
		if (entityId) {
			store.removeOne(entityId);
		}
		return;
	}

	if (type === RealtimeChangeType.Added || type === RealtimeChangeType.Updated) {
		if (!entityId) {
			return;
		}
		store.upsertOne({ ...payload, id: entityId });
		if (entityName === "workspaces") {
			applyWorkspaceUserPatch(stores, payload, currentWorkspaceId);
		}
	}
};

export interface ApplyRealtimeChangeResult {
	readonly relatedProjectId: string | null;
	readonly shouldInvalidateComego: boolean;
	readonly shouldInvalidateTimes: boolean;
	readonly touchedEntity: string | null;
	readonly touchedId: string | null;
}

export const EMPTY_APPLY_REALTIME_CHANGE_RESULT: ApplyRealtimeChangeResult = {
	relatedProjectId: null,
	shouldInvalidateComego: false,
	shouldInvalidateTimes: false,
	touchedEntity: null,
	touchedId: null,
};

const readRelatedProjectId = (payload: RealtimeEntityPayload): string | null => {
	const project = payload.project;
	if (project && typeof project === "object") {
		const id = (project as { readonly id?: unknown }).id;
		if (typeof id === "string" && id.length > 0) {
			return id;
		}
	}
	if (typeof payload.projectId === "string" && payload.projectId.length > 0) {
		return payload.projectId;
	}
	return null;
};

const isComegoPayloadForUser = (payload: RealtimeEntityPayload, userId: string, workspaceId: string): boolean =>
	resolveRealtimeEntityName(payload) === "comego" && payloadOwnerMatchesUser(payload, userId, workspaceId);

/**
 * Apply a SignalR/realtime `changes` message to web-api entity stores
 * (NotifyService parity: upsert/remove).
 */
export const applyRealtimeChange = (
	stores: RealtimeStores,
	message: RealtimeChangeMessage<RealtimeEntityPayload>,
	userId: string,
	workspaceId: string
): ApplyRealtimeChangeResult => {
	const type = normalizeRealtimeChangeType(message?.type);
	const payload = message?.payload;
	if (!(type && payload) || typeof payload !== "object") {
		return EMPTY_APPLY_REALTIME_CHANGE_RESULT;
	}

	const entityName = resolveRealtimeEntityName(payload);
	if (!entityName) {
		return EMPTY_APPLY_REALTIME_CHANGE_RESULT;
	}

	const touchedId = readEntityId(payload as Record<string, unknown>);
	const relatedProjectId = readRelatedProjectId(payload);

	if (entityName === "times") {
		const sameWorkspace = payload.workspace?.id === workspaceId;
		if (!sameWorkspace) {
			return EMPTY_APPLY_REALTIME_CHANGE_RESULT;
		}
		return {
			shouldInvalidateTimes: isTimesPayloadForUser(payload, userId, workspaceId),
			shouldInvalidateComego: false,
			touchedEntity: "times",
			touchedId,
			relatedProjectId,
		};
	}

	if (entityName === "comego") {
		const sameWorkspace = payload.workspace?.id === workspaceId;
		if (!sameWorkspace) {
			return EMPTY_APPLY_REALTIME_CHANGE_RESULT;
		}
		return {
			shouldInvalidateTimes: false,
			shouldInvalidateComego: isComegoPayloadForUser(payload, userId, workspaceId),
			touchedEntity: "comego",
			touchedId,
			relatedProjectId: null,
		};
	}

	if (entityName === "users") {
		if (type === RealtimeChangeType.Added || type === RealtimeChangeType.Updated) {
			applyUserChange(stores, payload, userId);
		}
		return {
			shouldInvalidateTimes: false,
			shouldInvalidateComego: false,
			touchedEntity: "users",
			touchedId,
			relatedProjectId: null,
		};
	}

	if (entityName === "notifications") {
		return {
			shouldInvalidateTimes: false,
			shouldInvalidateComego: false,
			touchedEntity: "notifications",
			touchedId,
			relatedProjectId: null,
		};
	}

	if (isEntityStoreKey(entityName)) {
		applyGenericEntityChange(stores, type, payload, entityName, workspaceId);
		return {
			shouldInvalidateTimes: false,
			shouldInvalidateComego: false,
			touchedEntity: entityName,
			touchedId,
			relatedProjectId,
		};
	}

	return EMPTY_APPLY_REALTIME_CHANGE_RESULT;
};
